import { ButtonProps as MagneticButtonProps } from "@magnetic/button";

/**
 * This makes name or id a mandatory prop.
 * It should help automation
 * tools identify buttons more easily.
 */
type RequireNameOrId =
  | { name: string; id?: never }
  | { id: string; name?: never }
  | { name: string; id: string };

/**
 * combine our RequireNameOrId type
 * with the MagneticButtonProps to
 * force name or id to be required
 * on buttons.
 */
export type ButtonProps = MagneticButtonProps &
  RequireNameOrId &
  Omit<MagneticButtonProps, "name" | "id">;

export enum ButtonScope {
  writeAccess,
  fabricEditModeOnly,
  adminOnly
}

export type EditButtonProps = {
  /**
   * Controls which guards are applied to the button.
   *
   * - `fabricEditModeOnly` (default): Hidden from read-only users,
   *   only visible when the fabric is in candidate-edit mode
   *   (CandidateMode.Edit), and disabled during software-upgrade
   *   maintenance mode. The read-only guard uses LoginContext;
   *   the edit-mode and software-upgrade guards rely on
   *   DataProviderContext, which is fabric-scoped.
   *
   * - `writeAccess`: Only the read-only guard applies.
   *   Use on pages outside a fabric context (e.g. notifications,
   *   API tokens, subscriptions, inventory).
   *
   * - `adminOnly`: Hidden from both read-only and read-write users.
   *   Only visible to users with the ADMIN role.
   */
  readonly scope?: ButtonScope;
  /**
   * Need a way to ignore the software mode check
   * logic or the unbind button will not be disabled
   * when we are in SOFTWARE_MODE_MAINTENANCE_ON.
   * unbind button should always be allowed if
   * in edit mode
   */
  readonly ignoreSoftwareMode?: boolean;
  readonly tooltip?: string;
  readonly disabled?: boolean;
} & ButtonProps;
